
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaFacebook, FaInstagram } from "react-icons/fa";
import { onAuthStateChanged, signOut } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import {
  LayoutDashboard,
  ShoppingBag,
  Package,
  Wallet,
  Users,

  Store,
  Menu,
  X,
  Bell,
  Search,
  ChevronRight,
  Copy,
  TrendingUp,
  IndianRupee,
  ShoppingCart,
  ArrowUpRight,
  ArrowDownLeft,
  Clock,
  CheckCircle2,
  Truck,
  CircleHelp,
  LogOut,
  Heart,
  SlidersHorizontal,
  Share2,
  LoaderCircle,
  RefreshCw,
  MessageCircle,
  Download,
  FileText,
} from "lucide-react";

import { auth, db } from "../../firebase";

const navigation = [
  { label: "Overview", icon: LayoutDashboard },
  { label: "Sarees & My Profit", icon: ShoppingBag },
  { label: "My Orders", icon: Package },
  { label: "Wallet & Withdraw", icon: Wallet },
  { label: "Enquire", icon: CircleHelp },
];

const money = (value = 0) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

const dateLabel = (value) => {
  if (!value) return "—";

  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : new Date(value);

  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

function StatCard({ icon: Icon, label, value, note, tone = "rose" }) {
  const tones = {
    rose: "bg-rose-50 text-rose-900",
    gold: "bg-amber-50 text-amber-800",
    green: "bg-emerald-50 text-emerald-700",
    blue: "bg-blue-50 text-blue-700",
  };

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm text-stone-500">{label}</p>
          <p className="mt-2 break-words text-2xl font-bold text-stone-900">
            {value}
          </p>
          <p className="mt-2 text-xs text-stone-500">{note}</p>
        </div>

        <div className={`rounded-xl p-3 ${tones[tone] || tones.rose}`}>
          <Icon size={21} />
        </div>
      </div>


    </div>
  );
}

function StatusBadge({ status = "Pending" }) {
  const normalized = String(status).toLowerCase();

  const style = normalized.includes("deliver")
    ? "bg-emerald-50 text-emerald-700"
    : normalized.includes("ship")
      ? "bg-blue-50 text-blue-700"
      : normalized.includes("process") || normalized.includes("pack")
        ? "bg-amber-50 text-amber-800"
        : "bg-stone-100 text-stone-600";

  return (
    <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${style}`}>
      {status}
    </span>
  );
}

function EmptyState({ title, detail }) {
  return (
    <div className="rounded-2xl border border-dashed border-stone-300 bg-white p-8 text-center">
      <Package className="mx-auto text-stone-400" size={28} />
      <p className="mt-3 font-semibold text-stone-800">{title}</p>
      <p className="mt-1 text-sm text-stone-500">{detail}</p>
    </div>
  );
}

function SectionTitle({ eyebrow, title, description }) {
  return (
    <div className="mb-6">
      <p className="text-xs font-bold uppercase tracking-[0.18em] text-rose-800">
        {eyebrow}
      </p>
      <h2 className="mt-1 font-serif text-2xl text-rose-950 sm:text-3xl">
        {title}
      </h2>
      <p className="mt-2 text-sm leading-6 text-stone-500">{description}</p>
    </div>
  );
}


const loadJsPDF = () =>
  new Promise((resolve, reject) => {
    if (window.jspdf?.jsPDF) return resolve(window.jspdf.jsPDF);
    const existing = document.querySelector('script[data-vasthra-jspdf="true"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(window.jspdf?.jsPDF));
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
    script.async = true;
    script.dataset.vasthraJspdf = "true";
    script.onload = () =>
      window.jspdf?.jsPDF
        ? resolve(window.jspdf.jsPDF)
        : reject(new Error("PDF library did not load."));
    script.onerror = () => reject(new Error("Could not load the PDF library. Check your internet connection."));
    document.head.appendChild(script);
  });

const buildOrderInvoicePdf = async (order) => {
  const JsPDF = await loadJsPDF();
  const pdf = new JsPDF({ unit: "mm", format: "a4" });
  const savedInvoice = order.invoice || order.result?.invoice || {};
  const invoiceCustomer = savedInvoice.customer || {};
  const orderCustomer = order.customer || {};
  // Checkout orders may store shipping details on the order itself rather than
  // inside invoice.customer. Prefer invoice data, then customer object, then
  // the common order-level shipping/customer fields.
  const customer = {
    ...orderCustomer,
    ...invoiceCustomer,
    name: invoiceCustomer.name || orderCustomer.name || order.customerName || order.name || "",
    phone: invoiceCustomer.phone || orderCustomer.phone || order.customerPhone || order.phone || "",
    email: invoiceCustomer.email || orderCustomer.email || order.customerEmail || order.email || "",
    address: invoiceCustomer.address || orderCustomer.address || order.shippingAddress || order.deliveryAddress || order.address || "",
    city: invoiceCustomer.city || orderCustomer.city || order.shippingCity || order.deliveryCity || order.city || "",
    state: invoiceCustomer.state || orderCustomer.state || order.shippingState || order.state || "",
    pincode: invoiceCustomer.pincode || orderCustomer.pincode || order.shippingPincode || order.deliveryPincode || order.pincode || "",
  };
  const items = savedInvoice.items || order.items || [];
  const orderId = (savedInvoice.orderIds || order.orderIds || [order.orderNumber || order.id]).filter(Boolean).join(", ");
  const paymentId = savedInvoice.razorpayPaymentId || order.razorpayPaymentId || order.paymentId || "Not available";
  const razorpayOrderId = savedInvoice.razorpayOrderId || order.razorpayOrderId || "Not available";
  const total = Number(savedInvoice.total ?? order.customerOrderTotal ?? order.totalAmount ?? order.amount ?? 0);
  const format = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const dateValue = savedInvoice.date || order.paidAt || order.createdAt || order.date || new Date();
  const date = typeof dateValue?.toDate === "function" ? dateValue.toDate() : new Date(dateValue);
  const dateText = Number.isNaN(date.getTime()) ? String(dateValue) : date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 16;
  const right = pageWidth - margin;
  let y = 0;

  const addFooter = () => {
    pdf.setDrawColor(232, 220, 224);
    pdf.line(margin, pageHeight - 18, right, pageHeight - 18);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(125, 105, 112);
    pdf.text("Thank you for shopping with Vasthra.", margin, pageHeight - 11);
    pdf.text("vasthra.online", right, pageHeight - 11, { align: "right" });
  };
  const ensureSpace = (needed = 12) => {
    if (y + needed > pageHeight - 25) {
      addFooter();
      pdf.addPage();
      y = 18;
    }
  };
  const label = (text, x, yy) => {
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);
    pdf.setTextColor(125, 105, 112);
    pdf.text(text.toUpperCase(), x, yy);
  };
  const value = (text, x, yy, opts = {}) => {
    pdf.setFont("helvetica", opts.bold ? "bold" : "normal");
    pdf.setFontSize(opts.size || 10);
    pdf.setTextColor(45, 35, 39);
    const lines = pdf.splitTextToSize(String(text || "—"), opts.width || 80);
    pdf.text(lines, x, yy);
    return lines.length;
  };

  // Branded header
  pdf.setFillColor(91, 24, 52);
  pdf.rect(0, 0, pageWidth, 48, "F");
  pdf.setTextColor(255, 255, 255);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(25);
  pdf.text("VASTHRA", margin, 20);
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(9);
  pdf.setTextColor(245, 225, 233);
  pdf.text("ETHNIC WEAR  •  SAREES", margin, 28);
  pdf.text("vasthra.online", margin, 35);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(18);
  pdf.setTextColor(255, 255, 255);
  pdf.text("INVOICE", right, 20, { align: "right" });
  pdf.setFillColor(220, 252, 231);
  pdf.roundedRect(right - 25, 27, 25, 8, 3, 3, "F");
  pdf.setTextColor(91, 24, 52);
  pdf.setFontSize(8);
  pdf.text("PAID", right - 12.5, 32.3, { align: "center" });

  y = 61;
  label("Invoice date", margin, y);
  value(dateText, margin, y + 6, { bold: true });
  label("Order reference", 78, y);
  value(orderId || "Not available", 78, y + 6, { bold: true, width: 115 });
  y += 22;

  // Customer details panel
  pdf.setFillColor(250, 246, 247);
  pdf.roundedRect(margin, y, pageWidth - margin * 2, 39, 3, 3, "F");
  label("Billed to", margin + 5, y + 8);
  value(customer.name || order.customerName || "Customer", margin + 5, y + 15, { bold: true, size: 11, width: 80 });
  const contact = [customer.phone || order.customerPhone, customer.email || order.customerEmail].filter(Boolean).join("  •  ");
  value(contact || "Contact details not provided", margin + 5, y + 22, { size: 8, width: 165 });
  const address = [customer.address, customer.city, customer.state, customer.pincode].filter(Boolean).join(", ");
  value(address || "Delivery address not provided", margin + 5, y + 29, { size: 8, width: 165 });
  y += 50;

  // Items table
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(8);
  pdf.setTextColor(255, 255, 255);
  pdf.setFillColor(91, 24, 52);
  pdf.roundedRect(margin, y, pageWidth - margin * 2, 11, 2, 2, "F");
  pdf.text("ITEM DESCRIPTION", margin + 4, y + 7);
  pdf.text("QTY", 126, y + 7, { align: "center" });
  pdf.text("UNIT PRICE", 153, y + 7, { align: "right" });
  pdf.text("AMOUNT", right - 4, y + 7, { align: "right" });
  y += 11;

  if (!items.length) {
    pdf.setTextColor(75, 65, 69);
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.text("Order item details are not available.", margin + 4, y + 9);
    y += 17;
  } else {
    items.forEach((item, index) => {
      const quantity = Number(item.quantity || 1);
      const price = Number(item.sellingPrice ?? item.price ?? item.unitPrice ?? 0);
      const lineTotal = Number(item.lineTotal ?? price * quantity);
      const itemName = item.productName || item.name || item.title || `Saree ${index + 1}`;
      const nameLines = pdf.splitTextToSize(String(itemName), 82);
      const rowHeight = Math.max(14, nameLines.length * 5 + 8);
      ensureSpace(rowHeight + 3);
      if (y === 18) {
        // Repeat the table heading when a long invoice continues on another page.
        pdf.setFillColor(91, 24, 52);
        pdf.rect(0, 0, pageWidth, 10, "F");
        pdf.setTextColor(255, 255, 255);
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(8);
        pdf.text("VASTHRA  |  INVOICE CONTINUED", margin, 6.5);
        y = 20;
      }
      if (index % 2 === 1) {
        pdf.setFillColor(252, 249, 250);
        pdf.rect(margin, y, pageWidth - margin * 2, rowHeight, "F");
      }
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(45, 35, 39);
      pdf.text(nameLines, margin + 4, y + 7);
      pdf.setFontSize(8.5);
      pdf.text(String(quantity), 126, y + 7, { align: "center" });
      pdf.text(format(price), 153, y + 7, { align: "right" });
      pdf.setFont("helvetica", "bold");
      pdf.text(format(lineTotal), right - 4, y + 7, { align: "right" });
      pdf.setDrawColor(238, 229, 232);
      pdf.line(margin, y + rowHeight, right, y + rowHeight);
      y += rowHeight;
    });
  }

  y += 10;
  ensureSpace(40);
  const totalBoxX = 104;
  const totalBoxW = right - totalBoxX;
  pdf.setFillColor(250, 246, 247);
  pdf.roundedRect(totalBoxX, y, totalBoxW, 25, 3, 3, "F");
  label("Total paid", totalBoxX + 5, y + 9);
  pdf.setFont("helvetica", "bold");
  pdf.setFontSize(16);
  pdf.setTextColor(91, 24, 52);
  pdf.text(format(total), right - 5, y + 18, { align: "right" });
  y += 36;

  ensureSpace(27);
  label("Payment details", margin, y);
  y += 7;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);
  pdf.setTextColor(90, 78, 83);
  pdf.text(`Payment ID: ${paymentId}`, margin, y);
  y += 5;
  pdf.text(`Razorpay Order ID: ${razorpayOrderId}`, margin, y);
  y += 9;
  pdf.setDrawColor(232, 220, 224);
  pdf.line(margin, y, right, y);
  y += 7;
  pdf.setFont("helvetica", "italic");
  pdf.setFontSize(8);
  pdf.setTextColor(125, 105, 112);
  pdf.text("This is a computer-generated invoice and does not require a signature.", margin, y);
  addFooter();

  const blob = pdf.output("blob");
  const safeOrderId = String(order.orderNumber || order.id || "order").replace(/[^a-z0-9-_]/gi, "_");
  const filename = `Vasthra-Invoice-${safeOrderId}.pdf`;
  return { blob, file: new File([blob], filename, { type: "application/pdf" }), filename, orderId, total };
};

export default function ResellerDashboard() {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [productSettings, setProductSettings] = useState([]);
  const [orders, setOrders] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notificationsOpen, setNotificationsOpen] = useState(false);

  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState("");
  const [active, setActive] = useState("Overview");
  const [mobileMenu, setMobileMenu] = useState(false);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("All");
  const [profits, setProfits] = useState({});
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [upi, setUpi] = useState("");
  const [withdrawMessage, setWithdrawMessage] = useState("");
  const [notice, setNotice] = useState("");
  const [savingWithdrawal, setSavingWithdrawal] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [enquirySubject, setEnquirySubject] = useState("Order / delivery issue");
  const [enquiryMessage, setEnquiryMessage] = useState("");
  const [enquirySaving, setEnquirySaving] = useState(false);
  const [enquiryNotice, setEnquiryNotice] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [renewingSubscription, setRenewingSubscription] = useState(false);
  const [renewalMessage, setRenewalMessage] = useState("");

  useEffect(() => {
    let unsubscribeData = [];
    let cancelled = false;

    const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
      unsubscribeData.forEach((unsubscribe) => unsubscribe());
      unsubscribeData = [];

      setUser(currentUser);
      setProfile(null);
      setProducts([]);
      setProductSettings([]);
      setOrders([]);
      setTransactions([]);
      setWithdrawals([]);
      setNotifications([]);
      setEnquiries([]);
      setDataError("");

      if (!currentUser) {
        setLoading(false);
        navigate("/signin", { replace: true });
        return;
      }

      setLoading(true);

      try {
        const userSnapshot = await getDoc(
          doc(db, "users", currentUser.uid)
        );

        let profileData = userSnapshot.exists()
          ? userSnapshot.data()
          : null;

        if (!profileData) {
          const resellerSnapshot = await getDoc(
            doc(db, "resellers", currentUser.uid)
          );

          profileData = resellerSnapshot.exists()
            ? resellerSnapshot.data()
            : null;
        }

        if (cancelled) return;

        if (!profileData) {
          setDataError(
            "Your account profile was not found in users or resellers."
          );
          setLoading(false);
          return;
        }

        const role = String(
          profileData.role || profileData.accountType || ""
        ).toLowerCase();

        const status = String(
          profileData.status || profileData.approvalStatus || ""
        ).toLowerCase();

        if (role && !role.includes("reseller")) {
          if (role.includes("admin")) {
            navigate("/admin/dashboard", { replace: true });
          } else if (role.includes("shop")) {
            navigate("/shop-owner/dashboard", { replace: true });
          } else {
            navigate("/signin", { replace: true });
          }

          setLoading(false);
          return;
        }

        setProfile({ ...profileData, uid: currentUser.uid });

        const expiryValue =
          profileData.subscriptionExpiresAt ||
          profileData.subscriptionEndDate ||
          profileData.subscriptionValidUntil ||
          null;
        const expiryDate = expiryValue
          ? (typeof expiryValue?.toDate === "function" ? expiryValue.toDate() : new Date(expiryValue))
          : null;
        if (expiryDate && !Number.isNaN(expiryDate.getTime()) && expiryDate.getTime() <= Date.now()) {
          setLoading(false);
          return;
        }

        if (
          ["pending", "pending approval", "awaiting approval", "suspended", "rejected"].includes(status)
        ) {
          setLoading(false);
          return;
        }

        const subscribe = (collectionName, constraints, setter) => {
          const reference = constraints.length
            ? query(collection(db, collectionName), ...constraints)
            : collection(db, collectionName);

          const unsubscribe = onSnapshot(
            reference,
            (snapshot) => {
              if (cancelled) return;

              setter(
                snapshot.docs.map((item) => ({
                  id: item.id,
                  ...item.data(),
                }))
              );
            },
            (error) => {
              console.error(`Firestore ${collectionName}:`, error);

              if (!cancelled) {
                setDataError(
                  `Unable to read "${collectionName}". Check Firestore rules, collection names and required fields.`
                );
              }
            }
          );

          unsubscribeData.push(unsubscribe);
        };

        // Catalogue products are read separately; the UI filters out hidden/unavailable items.
        subscribe("products", [], setProducts);

        // Each reseller only reads their own saved margins/listed products.
        subscribe(
          "resellerProductSettings",
          [where("resellerId", "==", currentUser.uid)],
          setProductSettings
        );

        subscribe(
          "orders",
          [where("resellerId", "==", currentUser.uid)],
          setOrders
        );

        subscribe(
          "transactions",
          [where("userId", "==", currentUser.uid)],
          setTransactions
        );

        subscribe(
          "withdrawals",
          [where("resellerId", "==", currentUser.uid)],
          setWithdrawals
        );

        // Notification feed is scoped to the signed-in reseller.
        subscribe(
          "notifications",
          [where("userId", "==", currentUser.uid)],
          setNotifications
        );

        // Live enquiry history, including replies written by the admin.
        subscribe(
          "enquiries",
          [where("userId", "==", currentUser.uid)],
          setEnquiries
        );
      } catch (error) {
        console.error("Reseller dashboard error:", error);

        if (!cancelled) {
          setDataError(
            error.code === "permission-denied"
              ? "Firestore access denied. Check your Firebase security rules."
              : "Unable to load your Firebase profile. Check the configuration and Firestore setup."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      unsubscribeAuth();
      unsubscribeData.forEach((unsubscribe) => unsubscribe());
    };
  }, [navigate]);

  const status = String(
    profile?.status || profile?.approvalStatus || ""
  ).toLowerCase();
  const subscriptionExpiryValue =
    profile?.subscriptionExpiresAt ||
    profile?.subscriptionEndDate ||
    profile?.subscriptionValidUntil ||
    null;
  const subscriptionExpiryDate = subscriptionExpiryValue
    ? (typeof subscriptionExpiryValue?.toDate === "function"
        ? subscriptionExpiryValue.toDate()
        : new Date(subscriptionExpiryValue))
    : null;
  const subscriptionExpired = Boolean(
    subscriptionExpiryDate &&
    !Number.isNaN(subscriptionExpiryDate.getTime()) &&
    subscriptionExpiryDate.getTime() <= Date.now()
  );

  const startSubscriptionRenewal = async () => {
    if (!user || renewingSubscription) return;
    setRenewingSubscription(true);
    setRenewalMessage("");
    try {
      const token = await user.getIdToken();
      const orderResponse = await fetch("/.netlify/functions/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ purpose: "reseller_renewal" }),
      });
      const orderText = await orderResponse.text();
      let orderData;
      try { orderData = JSON.parse(orderText); } catch { throw new Error("Payment function returned HTML instead of JSON. Start the app with `netlify dev` so Netlify Functions are available."); }
      if (!orderResponse.ok) throw new Error(orderData.error || "Could not create renewal order.");
      if (!window.Razorpay) {
        await new Promise((resolve, reject) => {
          const existing = document.querySelector('script[data-razorpay-checkout="true"]');
          if (existing) { existing.addEventListener("load", resolve, { once: true }); existing.addEventListener("error", reject, { once: true }); return; }
          const script = document.createElement("script");
          script.src = "https://checkout.razorpay.com/v1/checkout.js";
          script.async = true;
          script.dataset.razorpayCheckout = "true";
          script.onload = resolve;
          script.onerror = () => reject(new Error("Could not load Razorpay checkout. Check your internet connection and try again."));
          document.body.appendChild(script);
        });
      }
      if (!window.Razorpay) throw new Error("Razorpay checkout did not load. Please try again.");
      await new Promise((resolve, reject) => {
        const checkout = new window.Razorpay({
          key: orderData.keyId || process.env.REACT_APP_RAZORPAY_KEY_ID,
          amount: orderData.amount,
          currency: orderData.currency || "INR",
          name: "Vasthra",
          description: "Reseller store — one month renewal",
          order_id: orderData.razorpayOrderId,
          prefill: { name: profile?.name || "", email: profile?.email || user.email || "", contact: profile?.mobile || "" },
          theme: { color: "#881337" },
          handler: async (response) => {
            try {
              const verifyResponse = await fetch("/.netlify/functions/verify-payment", {
                method: "POST",
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify({
                  purpose: "reseller_renewal", draftId: orderData.draftId,
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                }),
              });
              const verifyText = await verifyResponse.text();
              let verifyData;
              try { verifyData = JSON.parse(verifyText); } catch { throw new Error("Payment verification returned HTML instead of JSON. Check Netlify Functions and deploy status."); }
              if (!verifyResponse.ok || !verifyData.success) throw new Error(verifyData.error || "Payment verification failed. Contact support before paying again.");
              const newExpiry = new Date(verifyData.subscriptionExpiresAt);
              setProfile((previous) => ({ ...previous, subscriptionStartedAt: new Date(), subscriptionExpiresAt: newExpiry, subscriptionStatus: "active", subscriptionPlan: "monthly", subscriptionAmount: 499 }));
              setRenewalMessage(`Payment successful. Your store is reactivated until ${newExpiry.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}.`);
              resolve();
            } catch (error) { setRenewalMessage(error.message || "Could not verify renewal payment."); reject(error); }
            finally { setRenewingSubscription(false); }
          },
          modal: { ondismiss: () => { setRenewingSubscription(false); resolve(); } },
        });
        checkout.on("payment.failed", (event) => { const message = event.error?.description || "Payment failed. Please try again."; setRenewalMessage(message); setRenewingSubscription(false); reject(new Error(message)); });
        checkout.open();
      });
    } catch (error) {
      setRenewalMessage(error.message || "Could not start renewal. Please try again.");
      setRenewingSubscription(false);
    }
  };

  const isPending = [
    "pending",
    "pending approval",
    "awaiting approval",
  ].includes(status);

  const isSuspended = ["suspended", "rejected"].includes(status);

  const storeSlug =
    profile?.storeSlug || profile?.slug || profile?.store?.slug || "";

  const storeUrl = storeSlug
    ? `${window.location.origin}/s/${storeSlug}`
    : "";

  const resellerName =
    profile?.shopName ||
    profile?.storeName ||
    profile?.name ||
    user?.displayName ||
    "Reseller";

  const initials =
    resellerName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase() || "R";

  const liveProducts = useMemo(
    () =>
      products.filter((product) => {
        const productStatus = String(product.status || "active").toLowerCase();
        const visibility = String(product.visibility || "live").toLowerCase();
        const stock = Number(product.stock ?? product.stockQuantity ?? 0);

        return (
          ["active", "live", "published", "approved"].includes(productStatus) &&
          !["hidden", "inactive", "disabled", "archived", "draft"].includes(visibility) &&
          stock > 0
        );
      }),
    [products]
  );

  const settingsByProduct = useMemo(
    () => Object.fromEntries(productSettings.map((setting) => [setting.productId, setting])),
    [productSettings]
  );

  const categories = [
    "All",
    ...new Set(
      liveProducts.map((product) =>
        String(product.type || product.category || "Other")
      )
    ),
  ];

  const filteredProducts = useMemo(
    () =>
      liveProducts.filter((product) => {
        const name = String(product.name || product.title || "");
        const type = String(product.type || product.category || "Other");

        return (
          name.toLowerCase().includes(search.toLowerCase()) &&
          (category === "All" || type === category)
        );
      }),
    [liveProducts, search, category]
  );

  const orderProfit = (order) =>
    Number(order.resellerEarning ?? order.resellerProfit ?? order.profit ?? order.profitAmount ?? 0);

  const orderAmount = (order) =>
    Number(
      order.customerPaid ??
        order.totalAmount ??
        order.sellingPrice ??
        order.amount ??
        0
    );

  const deliveredOrders = orders.filter(
    (order) => String(order.status || order.orderStatus || "").toLowerCase() === "delivered"
  );

  // Use the saved wallet when present; if older profiles have no wallet fields,
  // derive a display fallback from delivered orders so the dashboard is not stuck at ₹0.
  const deliveredProfitFallback = deliveredOrders.reduce(
    (sum, order) => sum + orderProfit(order), 0
  );
  const savedWalletBalance = profile?.walletBalance ?? profile?.wallet?.balance;
  const savedTotalEarnings = profile?.totalEarnings ?? profile?.wallet?.totalEarned;
  const walletBalanceIsSaved = savedWalletBalance != null && Number(savedWalletBalance) > 0;
  // Prefer the saved wallet (admin payout flow deducts from it). Only derive a
  // fallback for older profiles, and subtract both reserved and already-paid
  // withdrawals so a paid amount cannot reappear as available balance.
  const fallbackWithdrawalTotal = withdrawals
    .filter((item) => ["pending", "processing", "approved", "paid"].includes(String(item.status || "").toLowerCase()))
    .reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const walletBalance = Math.max(0, walletBalanceIsSaved
    ? Number(savedWalletBalance)
    : deliveredProfitFallback - fallbackWithdrawalTotal);
  const totalEarnings = Number(
    (savedTotalEarnings == null || Number(savedTotalEarnings) === 0) && deliveredProfitFallback > 0 ? deliveredProfitFallback : (savedTotalEarnings ?? 0)
  );

  const pendingEarnings = orders
    .filter(
      (order) =>
        !["delivered", "cancelled", "refunded"].includes(
          String(order.status || "").toLowerCase()
        )
    )
    .reduce((sum, order) => sum + orderProfit(order), 0);

  const customers = new Set(
    orders
      .map(
        (order) =>
          order.customerId || order.customerPhone || order.customerEmail
      )
      .filter(Boolean)
  ).size;

  const recentOrders = [...orders]
    .sort((a, b) => {
      const time = (value) =>
        value?.toDate
          ? value.toDate().getTime()
          : new Date(value || 0).getTime();

      return time(b.createdAt || b.date) - time(a.createdAt || a.date);
    })
    .slice(0, 5);

  const recentTransactions = [...transactions]
    .sort((a, b) => {
      const time = (value) =>
        value?.toDate
          ? value.toDate().getTime()
          : new Date(value || 0).getTime();

      return time(b.createdAt || b.date) - time(a.createdAt || a.date);
    })
    .slice(0, 5);

  const goTo = (label) => {
    setActive(label);
    setMobileMenu(false);
    setNotice("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const copyText = async (text, successMessage) => {
    if (!text) {
      setNotice("This information has not been configured in your profile.");
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setNotice(successMessage);
    } catch {
      setNotice(`Copy this text manually: ${text}`);
    }
  };

  const shareOrderStatus = (order) => {
    const phone = String(order.customerPhone || order.customer?.phone || "").replace(/\\D/g, "");
    if (!phone) {
      setNotice("Customer phone number is not available for this order.");
      return;
    }

    // WhatsApp expects country code. If an Indian 10-digit number is stored,
    // add India's country code; preserve an existing country code.
    const whatsappPhone = phone.length === 10 ? `91${phone}` : phone;
    const currentStatus = String(order.orderStatus || order.status || "processing").replace(/_/g, " ");
    const orderRef = order.orderNumber || order.id;
    const message = `Hi ${order.customerName || order.customer?.name || "there"}, update for your Vasthra order ${orderRef}: ${currentStatus}.${order.trackingNumber ? ` Tracking number: ${order.trackingNumber}.` : ""} Please contact us if you need help.`;
    window.open(`https://wa.me/${whatsappPhone}?text=${encodeURIComponent(message)}`, "_blank", "noopener,noreferrer");
  };

  const downloadOrderInvoice = async (order) => {
    try {
      const invoicePdf = await buildOrderInvoicePdf(order);
      const url = URL.createObjectURL(invoicePdf.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = invoicePdf.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      setNotice("Invoice downloaded. You can now share the PDF from your device.");
    } catch (error) {
      console.error("Invoice PDF download failed:", error);
      setNotice(error.message || "Could not generate the invoice. Please try again.");
    }
  };

  const shareOrderInvoiceWhatsApp = async (order) => {
    try {
      const invoicePdf = await buildOrderInvoicePdf(order);
      const message = `Vasthra invoice for order ${invoicePdf.orderId}. Total paid: ${money(invoicePdf.total)}. Payment ID: ${order.razorpayPaymentId || order.paymentId || order.result?.invoice?.razorpayPaymentId || "available in order details"}.`;
      if (navigator.canShare?.({ files: [invoicePdf.file] }) && navigator.share) {
        await navigator.share({ files: [invoicePdf.file], title: "Vasthra Invoice", text: message });
        return;
      }
      const url = URL.createObjectURL(invoicePdf.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = invoicePdf.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      const phone = String(order.customerPhone || order.customer?.phone || "").replace(/\D/g, "");
      const whatsappPhone = phone.length === 10 ? `91${phone}` : phone;
      const shareUrl = `https://wa.me/${whatsappPhone}?text=${encodeURIComponent(`${message} I have downloaded the invoice PDF and will attach it here.`)}`;
      window.open(shareUrl, "_blank", "noopener,noreferrer");
      setNotice("Invoice downloaded. WhatsApp opened; attach the downloaded PDF in the chat if it is not attached automatically.");
    } catch (error) {
      if (error?.name === "AbortError") return;
      console.error("WhatsApp invoice share failed:", error);
      setNotice(error.message || "Could not share invoice. Please download it and attach it in WhatsApp.");
    }
  };

  const shareProduct = async (product, sellingPrice) => {
    const name = product.name || product.title || "Saree";
    const text = `Check out ${name} at ${money(sellingPrice)}!`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: name,
          text,
          url: storeUrl || window.location.origin,
        });
      } catch {
        // The user may close the share dialog.
      }
    } else {
      await copyText(
        `${text} ${storeUrl}`,
        "Product sharing text copied."
      );
    }
  };

  const saveProductSetting = async (product, marginValue) => {
    if (!user?.uid) {
      setNotice("Please sign in again before saving your margin.");
      return;
    }

    const margin = Number(marginValue);
    const basePrice = Number(
      product.wholesalePrice ?? product.fixedPrice ?? product.price ?? 0
    );

    if (!Number.isFinite(margin) || margin < 0 || margin > 100000) {
      setNotice("Enter a valid margin between ₹0 and ₹1,00,000.");
      return;
    }
    if (!Number.isFinite(basePrice) || basePrice <= 0) {
      setNotice("This product does not have a valid wholesale/fixed price yet.");
      return;
    }

    try {
      const settingId = `${user.uid}_${product.id}`;
      await setDoc(doc(db, "resellerProductSettings", settingId), {
        resellerId: user.uid,
        resellerName,
        productId: product.id,
        productName: product.name || product.title || "Saree",
        shopOwnerId: product.shopOwnerId || "",
        margin,
        sellingPrice: basePrice + margin,
        isListed: true,
        updatedAt: serverTimestamp(),
      }, { merge: true });

      setNotice("Margin saved. This saree is now listed on your public store.");
    } catch (error) {
      console.error("Save reseller product margin failed:", error);
      setNotice(
        error.code === "permission-denied"
          ? "Firebase rules blocked saving your margin. Add the resellerProductSettings rules provided with this update."
          : "Could not save this margin. Please try again."
      );
    }
  };

  const requestWithdrawal = async (event) => {
    event.preventDefault();
    setWithdrawMessage("");

    const amount = Number(withdrawAmount);

    if (!user || !profile) {
      setWithdrawMessage("Please sign in again.");
      return;
    }

    if (!/^[\w.-]+@[\w.-]+$/.test(upi.trim())) {
      setWithdrawMessage("Enter a valid UPI ID.");
      return;
    }

    if (!Number.isFinite(amount) || amount < 100) {
      setWithdrawMessage("Minimum withdrawal request is ₹100.");
      return;
    }

    if (amount > walletBalance) {
      setWithdrawMessage("The requested amount exceeds your displayed balance.");
      return;
    }

    setSavingWithdrawal(true);

    try {
      await addDoc(collection(db, "withdrawals"), {
        resellerId: user.uid,
        resellerName,
        amount,
        upiId: upi.trim(),
        status: "pending",
        createdAt: serverTimestamp(),
      });

      setWithdrawMessage(
        "Request submitted. The admin must review it before any payout."
      );

      setWithdrawAmount("");
    } catch (error) {
      console.error("Withdrawal request failed:", error);

      setWithdrawMessage(
        error.code === "permission-denied"
          ? "Firebase rules blocked this request. Check withdrawals permissions."
          : "Could not submit the request. Please try again."
      );
    } finally {
      setSavingWithdrawal(false);
    }
  };

  const unreadNotifications = notifications.filter((item) => item.isRead !== true);

  const markNotificationRead = async (notification) => {
    if (!user?.uid || notification.isRead === true) return;
    try {
      await updateDoc(doc(db, "notifications", notification.id), {
        isRead: true,
        readAt: serverTimestamp(),
      });
    } catch (error) {
      console.error("Mark notification read failed:", error);
      setNotice(error.code === "permission-denied"
        ? "Notification read permission is blocked by Firestore rules."
        : "Could not update this notification.");
    }
  };

  const openInstagramShare = () => {
    if (!storeUrl) {
      setNotice("Your store link is not available yet.");
      return;
    }
    // Instagram has no general web share URL for arbitrary store links. Copy the link
    // and open Instagram in the same click so mobile browsers do not block the popup.
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
    void copyText(storeUrl, "Store link copied. Paste it into your Instagram bio, story link sticker or message.");
    setShareModalOpen(false);
  };

  const logout = async () => {
    try {
      await signOut(auth);
      navigate("/signin", { replace: true });
    } catch (error) {
      console.error("Logout failed:", error);
      setNotice("Could not log out. Please try again.");
    }
  };

  const openStoreShare = () => {
    if (!storeUrl) {
      setNotice("Your store link is not available yet. Please check your store slug in your profile.");
      return;
    }
    setShareModalOpen(true);
  };

  const shareText = `Visit my saree store ${resellerName}: ${storeUrl}`;
  const whatsappShareUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(storeUrl || "")}`;

  const sidebar = (
    <>
      <div className="flex items-center gap-3 px-5 py-6">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-amber-300 text-rose-950">
          <Heart size={23} fill="currentColor" />
        </div>
        <div>
          <p className="font-serif text-xl font-bold text-white">
            Vasthra
          </p>
          <p className="text-xs text-rose-200">Reseller workspace</p>
        </div>
      </div>

      <div className="mx-4 mb-5 rounded-xl border border-white/10 bg-white/5 p-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-200 font-bold text-rose-950">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-white">
              {resellerName}
            </p>
            <p className="text-xs text-rose-200">Reseller account</p>
          </div>
        </div>
        <p className="mt-3 truncate text-xs text-rose-200">{user?.email}</p>
      </div>

      <p className="px-5 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-rose-300">
        Workspace
      </p>

      <nav className="space-y-1 px-3">
        {navigation.map(({ label, icon: Icon }) => (
          <button
            key={label}
            onClick={() => goTo(label)}
            className={`flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left text-sm transition ${
              active === label
                ? "bg-amber-300 font-semibold text-rose-950"
                : "text-rose-100 hover:bg-white/10 hover:text-white"
            }`}
          >
            <Icon size={19} />
            <span>{label}</span>
            {active === label && (
              <ChevronRight className="ml-auto" size={16} />
            )}
          </button>
        ))}
      </nav>

      <div className="px-3 pt-6">
        <p className="px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-rose-300">
          Your website
        </p>

        <button
          onClick={() => goTo("My Store")}
          className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm text-rose-100 hover:bg-white/10"
        >
          <Store size={19} /> My store
        </button>
        {storeUrl && (
          <a href={storeUrl} target="_blank" rel="noreferrer" className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm text-amber-200 hover:bg-white/10">
            <ArrowUpRight size={19} /> Open my store
          </a>
        )}

      </div>

      <div className="mt-auto p-4">
        <div className="rounded-xl bg-white/5 p-4">
          <p className="text-sm font-semibold text-white">Grow your business</p>
          <p className="mt-1 text-xs leading-5 text-rose-200">
            Share your store and reach more customers.
          </p>
          <button
            onClick={openStoreShare}
            className="mt-3 flex items-center gap-2 text-xs font-bold text-amber-300"
          >
            <Share2 size={15} /> Share my store
          </button>
        </div>

        <button
          onClick={logout}
          className="mt-4 flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm text-rose-200 hover:bg-white/10"
        >
          <LogOut size={18} /> Log out
        </button>
      </div>
    </>
  );

  const renderOverview = () => (
    <>
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-rose-950 via-rose-900 to-rose-800 p-6 text-white sm:p-9">
        <div className="relative z-10 max-w-xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-xs text-amber-200">
            <TrendingUp size={14} /> Your business overview
          </span>

          <h2 className="mt-5 font-serif text-3xl leading-tight sm:text-4xl">
            Welcome, {resellerName}!
          </h2>

          <p className="mt-3 max-w-md text-sm leading-6 text-rose-100">
            Manage your catalogue, orders and earnings from one place.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={() => goTo("Sarees & My Profit")}
              className="rounded-xl bg-amber-300 px-5 py-3 text-sm font-bold text-rose-950 hover:bg-amber-200"
            >
              Explore sarees
            </button>

            <button
              onClick={openStoreShare}
              className="inline-flex items-center gap-2 rounded-xl border border-white/30 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10"
            >
              <Share2 size={16} /> Share my store
            </button>
            {storeUrl && (
              <a href={storeUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-bold text-rose-950 hover:bg-rose-50">
                <Store size={16} /> Open my store
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        <StatCard
          icon={ShoppingBag}
          label="Available sarees"
          value={liveProducts.length}
          note="Products loaded from Firestore"
        />
        <StatCard
          icon={Package}
          label="Total orders"
          value={orders.length}
          note="Orders linked to your account"
          tone="gold"
        />
        <StatCard
          icon={Users}
          label="Customers"
          value={customers}
          note="Unique customers in your orders"
          tone="blue"
        />
        <StatCard
          icon={Wallet}
          label="Wallet balance"
          value={money(walletBalance)}
          note="Saved reseller wallet balance"
          tone="green"
        />
        <StatCard
          icon={TrendingUp}
          label="Total earnings"
          value={money(totalEarnings)}
          note="Recorded earnings"
          tone="green"
        />
        <StatCard
          icon={IndianRupee}
          label="Pending earnings"
          value={money(pendingEarnings)}
          note="Estimate from incomplete orders"
          tone="gold"
        />
      </div>

      <div className="mt-8 grid gap-6 xl:grid-cols-2">
        <section className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-serif text-xl text-rose-950">Recent orders</h3>
            <button
              onClick={() => goTo("My Orders")}
              className="text-xs font-bold text-rose-800"
            >
              View all
            </button>
          </div>

          <div className="mt-5 space-y-4">
            {recentOrders.length ? (
              recentOrders.map((order) => (
                <div
                  key={order.id}
                  className="flex items-center gap-3 border-b border-stone-100 pb-4 last:border-0 last:pb-0"
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-800">
                    <ShoppingBag size={18} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold">
                      {order.customerName || order.customer?.name || "Customer"}
                    </p>
                    <p className="mt-1 truncate text-xs text-stone-500">
                      {order.orderNumber || order.id} ·{" "}
                      {order.productName || order.itemName || "Saree order"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold">{money(orderAmount(order))}</p>
                    <div className="mt-1">
                      <StatusBadge status={order.status} />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <EmptyState
                title="No orders yet"
                detail="Orders linked to your reseller account will appear here."
              />
            )}
          </div>
        </section>

        <section className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-serif text-xl text-rose-950">Wallet activity</h3>
            <button
              onClick={() => goTo("Wallet & Withdraw")}
              className="text-xs font-bold text-rose-800"
            >
              Details
            </button>
          </div>

          <div className="mt-5 space-y-4">
            {recentTransactions.length ? (
              recentTransactions.map((transaction) => {
                const credit = String(
                  transaction.type || transaction.direction || "credit"
                ).toLowerCase() === "credit";

                return (
                  <div key={transaction.id} className="flex items-center gap-3">
                    <div
                      className={`rounded-full p-2 ${
                        credit
                          ? "bg-emerald-50 text-emerald-700"
                          : "bg-rose-50 text-rose-800"
                      }`}
                    >
                      {credit ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">
                        {transaction.description || transaction.title || transaction.type || "Transaction"}
                      </p>
                      <p className="mt-1 text-xs text-stone-500">
                        {dateLabel(transaction.createdAt || transaction.date)}
                      </p>
                    </div>
                    <p className="text-sm font-bold">
                      {credit ? "+" : "−"}{money(transaction.amount)}
                    </p>
                  </div>
                );
              })
            ) : (
              <EmptyState
                title="No transactions yet"
                detail="Recorded wallet transactions will appear here."
              />
            )}
          </div>
        </section>
      </div>
    </>
  );

  const renderProducts = () => (
    <>
      <SectionTitle
        eyebrow="Your catalogue"
        title="Sarees & My Profit"
        description="Browse live sarees uploaded by shop owners, set your margin, and save selected sarees to your public store."
      />

      <div className="mb-4 grid grid-cols-1 gap-2 rounded-xl border border-stone-200 bg-white p-3 sm:grid-cols-[minmax(0,1fr)_220px] sm:items-center">
        <div className="relative flex-1">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
            size={18}
          />
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search sarees..."
            className="w-full rounded-lg border border-stone-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-rose-800"
          />
        </div>

        <div className="flex min-w-0 items-center gap-2">
          <SlidersHorizontal size={16} className="shrink-0 text-stone-500" />
          <select
            value={category}
            onChange={(event) => setCategory(event.target.value)}
            className="min-w-0 flex-1 rounded-lg border border-stone-200 bg-white px-2.5 py-2.5 text-sm sm:min-w-0"
          >
            {categories.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
      </div>

      {!filteredProducts.length ? (
        <EmptyState
          title="No sarees available"
          detail="Products from the Firestore products collection will appear here."
        />
      ) : (
        <div className="grid grid-cols-2 gap-2.5 sm:gap-4 lg:grid-cols-3 2xl:grid-cols-4">
          {filteredProducts.map((product) => {
            const id = product.id;
            const wholesale = Number(
              product.wholesalePrice ?? product.fixedPrice ?? product.price ?? 0
            );
            const savedSetting = settingsByProduct[id];
            const profit = Number(profits[id] ?? savedSetting?.margin ?? product.defaultProfit ?? 200);
            const sellingPrice = wholesale + profit;
            const isListed = Boolean(savedSetting?.isListed);
            const image =
              product.imageUrl || product.image || product.photoURL;

            return (
              <article
                key={id}
                className="min-w-0 overflow-hidden rounded-xl border border-stone-200 bg-white transition hover:shadow-lg"
              >
                <div className="flex aspect-[3/4] w-full items-center justify-center overflow-hidden bg-white p-1 sm:p-2">
                  {image ? (
                    <img
                      src={image}
                      alt={product.name || product.title || "Saree"}
                      loading="lazy"
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <ShoppingBag size={38} className="text-rose-300" />
                  )}
                </div>

                <div className="p-2.5 sm:p-4">
                  <span className="text-xs font-semibold text-rose-800">
                    {product.type || product.category || "Saree"}
                  </span>

                  <h3 className="mt-1 line-clamp-2 break-words font-serif text-base text-rose-950 sm:text-xl">
                    {product.name || product.title || "Untitled saree"}
                  </h3>

                  <p className="mt-2 text-xs leading-5 text-stone-500 sm:text-sm">
                    Wholesale price:{" "}
                    <strong className="text-stone-800">{money(wholesale)}</strong>
                  </p>

                  <div className="mt-3 rounded-lg bg-[#faf7f5] p-2 sm:mt-5 sm:rounded-xl sm:p-3">
                    <div className="flex items-center justify-between gap-3">
                      <label htmlFor={`profit-${id}`} className="text-[10px] font-semibold leading-4 text-stone-600 sm:text-xs">
                        Your proposed profit
                      </label>
                      <span className="text-xs font-bold text-emerald-700 sm:text-sm">
                        +{money(profit)}
                      </span>
                    </div>

                    <input
                      id={`profit-${id}`}
                      type="range"
                      min="0"
                      max="1000"
                      step="10"
                      value={Math.min(1000, Math.max(0, profit))}
                      onChange={(event) =>
                        setProfits((current) => ({
                          ...current,
                          [id]: Number(event.target.value),
                        }))
                      }
                      className="mt-3 w-full accent-rose-800"
                    />

                    <div className="mt-1 flex justify-between text-[10px] text-stone-400">
                      <span>₹0</span><span>₹1,000</span>
                    </div>
                  </div>

                  <div className="mt-3 flex items-end justify-between gap-3 sm:mt-4">
                    <div className="min-w-0">
                      <p className="text-[10px] leading-4 text-stone-500 sm:text-xs">Selling price preview</p>
                      <p className="mt-1 text-lg font-bold text-rose-950 sm:text-2xl">
                        {money(sellingPrice)}
                      </p>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-2">
                    <button
                      type="button"
                      onClick={() => saveProductSetting(product, profit)}
                      className="rounded-lg bg-emerald-700 px-2 py-2.5 text-[11px] font-bold leading-4 text-white hover:bg-emerald-800 sm:rounded-xl sm:px-4 sm:text-sm"
                    >
                      {isListed ? "Update margin" : "Save margin & list"}
                    </button>
                    {isListed && (
                      <span className="inline-flex items-center justify-center rounded-xl bg-emerald-50 px-3 py-2.5 text-xs font-semibold text-emerald-800">
                        Live on your store
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => shareProduct(product, sellingPrice)}
                      className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg bg-rose-800 px-2 py-2.5 text-[11px] font-bold text-white hover:bg-rose-950 sm:rounded-xl sm:px-4 sm:text-sm"
                    >
                      <Share2 size={15} /> Share
                    </button>
                  </div>
                  <p className="mt-2 text-[10px] leading-4 text-stone-400 sm:mt-3 sm:text-[11px] sm:leading-5">
                    Saving lists this saree on your store. Customer orders and final pricing still need trusted server-side validation.
                  </p>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </>
  );

  const renderOrders = () => (
    <>
      <SectionTitle
        eyebrow="Sales & fulfilment"
        title="My Orders"
        description="Orders associated with your reseller account."
      />

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatCard icon={ShoppingCart} label="Total orders" value={orders.length} note="Firestore records" />
        <StatCard
          icon={Clock}
          label="In progress"
          value={orders.filter((order) => !["delivered", "cancelled", "refunded"].includes(String(order.status || "").toLowerCase())).length}
          note="Not yet completed"
          tone="gold"
        />
        <StatCard icon={CheckCircle2} label="Delivered" value={deliveredOrders.length} note="Delivered orders" tone="green" />
      </div>

      {orders.length ? (
        <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white">
          <div className="divide-y divide-stone-100">
            {orders.map((order) => (
              <div key={order.id} className="p-4 hover:bg-stone-50 sm:p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold text-rose-800">{order.orderNumber || order.id}</p>
                    <h4 className="mt-1 font-semibold">{order.customerName || order.customer?.name || "Customer"}</h4>
                    <p className="mt-1 text-sm text-stone-500">
                      {Array.isArray(order.items) && order.items.length
                        ? order.items.map((item) => `${item.productName || item.name || "Saree"} × ${Number(item.quantity || 1)}`).join(", ")
                        : order.productName || order.itemName || "Saree order"}
                    </p>
                    <p className="mt-1 text-xs text-stone-500">
                      {order.customerPhone || order.customer?.phone || "Customer phone not provided"}
                    </p>
                    <p className="mt-2 text-xs text-stone-400">{dateLabel(order.createdAt || order.date)}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold">{money(order.customerOrderTotal ?? orderAmount(order))}</p>
                    <p className="mt-1 text-xs text-emerald-700">Your margin: {money(orderProfit(order))}</p>
                    <div className="mt-2"><StatusBadge status={String(order.orderStatus || order.status || "pending").replace(/_/g, " ")} /></div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs text-stone-500">
                    <Truck size={15} />
                    {order.trackingNumber ? `Tracking: ${order.trackingNumber}` : "Tracking information has not been added."}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => downloadOrderInvoice(order)}
                      className="inline-flex items-center gap-2 rounded-xl border border-rose-200 bg-white px-3 py-2 text-xs font-bold text-rose-900 hover:bg-rose-50"
                    >
                      <Download size={15} /> Download invoice
                    </button>
                    <button
                      type="button"
                      onClick={() => shareOrderInvoiceWhatsApp(order)}
                      className="inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-3 py-2 text-xs font-bold text-white hover:bg-emerald-800"
                    >
                      <FileText size={15} /> Share invoice
                    </button>
                    <button
                      type="button"
                      onClick={() => shareOrderStatus(order)}
                      className="inline-flex items-center gap-2 rounded-xl border border-emerald-200 px-3 py-2 text-xs font-bold text-emerald-800 hover:bg-emerald-50"
                    >
                      <MessageCircle size={15} /> WhatsApp customer
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <EmptyState title="No orders yet" detail="Orders linked to your reseller ID will appear here." />
      )}
    </>
  );

  const renderWallet = () => (
    <>
      <SectionTitle
        eyebrow="Your earnings"
        title="Wallet & Withdraw"
        description="Review your saved wallet balance and request a payout."
      />

      <div className="rounded-3xl bg-gradient-to-br from-rose-950 to-rose-800 p-6 text-white sm:p-8">
        <p className="text-sm text-rose-200">Available wallet balance</p>
        <h3 className="mt-3 font-serif text-4xl sm:text-5xl">{money(walletBalance)}</h3>
        <p className="mt-3 text-sm text-rose-100">
          Withdrawal requests require admin review.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <span className="rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-sm">
            Total earnings: {money(totalEarnings)}
          </span>
          <span className="rounded-xl border border-white/20 bg-white/10 px-4 py-2 text-sm">
            Pending estimate: {money(pendingEarnings)}
          </span>
        </div>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <form onSubmit={requestWithdrawal} className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
          <h3 className="font-serif text-xl text-rose-950">Request withdrawal</h3>
          <p className="mt-2 text-sm leading-6 text-stone-500">
            This creates a request for review. It does not transfer money automatically.
          </p>

          <label className="mt-5 block text-sm font-medium text-stone-700">
            UPI ID
            <input
              value={upi}
              onChange={(event) => setUpi(event.target.value)}
              placeholder="yourname@upi"
              required
              className="mt-2 w-full rounded-xl border border-stone-200 px-4 py-3 text-sm"
            />
          </label>

          <label className="mt-4 block text-sm font-medium text-stone-700">
            Amount (₹)
            <input
              type="number"
              min="100"
              max={walletBalance}
              step="1"
              value={withdrawAmount}
              onChange={(event) => setWithdrawAmount(event.target.value)}
              required
              className="mt-2 w-full rounded-xl border border-stone-200 px-4 py-3 text-sm"
            />
          </label>

          {withdrawMessage && (
            <p role="status" className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
              {withdrawMessage}
            </p>
          )}

          <button
            disabled={savingWithdrawal || !walletBalance}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-rose-800 px-5 py-3 font-semibold text-white hover:bg-rose-950 disabled:opacity-50"
          >
            {savingWithdrawal && <LoaderCircle className="animate-spin" size={17} />}
            {savingWithdrawal ? "Submitting..." : "Submit withdrawal request"}
          </button>
        </form>

        <div className="rounded-2xl border border-stone-200 bg-white p-5 sm:p-6">
          <h3 className="font-serif text-xl text-rose-950">Withdrawal history</h3>
          <div className="mt-5 space-y-4">
            {withdrawals.length ? (
              withdrawals.map((item) => (
                <div key={item.id} className="flex items-start gap-3 border-b border-stone-100 pb-4 last:border-0">
                  <div className="rounded-xl bg-rose-50 p-3 text-rose-800">
                    <Wallet size={19} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold">{money(item.amount)} · {item.upiId || "UPI"}</p>
                    <p className="mt-1 text-xs text-stone-500">{dateLabel(item.createdAt)}</p>
                  </div>
                  <StatusBadge status={item.status || "Pending"} />
                </div>
              ))
            ) : (
              <EmptyState title="No withdrawal requests" detail="Your submitted requests will appear here." />
            )}
          </div>
        </div>
      </div>

      <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
        Wallet deductions and payouts must be validated by trusted server-side code. This form only submits a request.
      </p>
    </>
  );

  const renderEnquiry = () => (
    <>
      <SectionTitle eyebrow="Support" title="Enquire / Support Questions" description="Submit an issue and check the admin reply below. Replies appear here automatically." />
      <form onSubmit={async (event) => {
        event.preventDefault(); setEnquiryNotice("");
        if (!user?.uid || !enquiryMessage.trim()) { setEnquiryNotice("Please enter your issue before submitting."); return; }
        setEnquirySaving(true);
        try {
          await addDoc(collection(db, "enquiries"), { userId: user.uid, uid: user.uid, accountType: "reseller", name: profile?.fullName || profile?.name || profile?.displayName || "Reseller", email: profile?.email || user.email || "", subject: enquirySubject, message: enquiryMessage.trim(), status: "new", adminReply: "", createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
          setEnquiryMessage(""); setEnquiryNotice("Enquiry submitted successfully. Admin will reply from the Enquiries section.");
        } catch (error) { console.error("Enquiry submit failed", error); setEnquiryNotice("Could not submit enquiry. Please check your connection or Firestore rules and try again."); }
        finally { setEnquirySaving(false); }
      }} className="max-w-3xl rounded-2xl border border-stone-200 bg-white p-5 sm:p-7">
        <label className="block text-sm font-semibold text-stone-700">What do you need help with?
          <select value={enquirySubject} onChange={(e) => setEnquirySubject(e.target.value)} className="mt-2 w-full rounded-xl border border-stone-300 px-4 py-3 font-normal"><option>Order / delivery issue</option><option>Wallet / withdrawal</option><option>Store / product issue</option><option>Payment issue</option><option>Account / login issue</option><option>Other</option></select>
        </label>
        <label className="mt-4 block text-sm font-semibold text-stone-700">Describe your issue
          <textarea required maxLength={3000} rows={5} value={enquiryMessage} onChange={(e) => setEnquiryMessage(e.target.value)} placeholder="Explain the issue and include an order ID if relevant..." className="mt-2 w-full rounded-xl border border-stone-300 px-4 py-3 font-normal" />
        </label>
        {enquiryNotice && <p role="status" className="mt-4 rounded-xl bg-rose-50 p-3 text-sm text-rose-900">{enquiryNotice}</p>}
        <button disabled={enquirySaving} className="mt-5 rounded-xl bg-rose-900 px-5 py-3 text-sm font-bold text-white disabled:opacity-50">{enquirySaving ? "Submitting..." : "Submit enquiry"}</button>
      </form>
      <section className="mt-6 max-w-3xl rounded-2xl border border-stone-200 bg-white p-5 sm:p-7">
        <h3 className="font-serif text-xl text-rose-950">My enquiries and replies</h3>
        {!enquiries.length ? <p className="mt-3 text-sm text-stone-500">You have not submitted any enquiries yet.</p> : <div className="mt-4 space-y-3">{[...enquiries].sort((a,b) => { const time = (v) => v?.toDate ? v.toDate().getTime() : new Date(v || 0).getTime(); return time(b.createdAt)-time(a.createdAt); }).map((item) => <article key={item.id} className="rounded-xl border border-stone-200 p-4"><div className="flex flex-wrap items-center justify-between gap-2"><p className="font-semibold text-stone-900">{item.subject || "Support enquiry"}</p><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${item.adminReply ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`}>{item.adminReply ? "Admin replied" : String(item.status || "new").replaceAll("_", " ")}</span></div><p className="mt-2 whitespace-pre-wrap text-sm text-stone-600">{item.message}</p>{item.adminReply ? <div className="mt-3 rounded-lg bg-emerald-50 p-3"><p className="text-xs font-bold text-emerald-800">Admin reply</p><p className="mt-1 whitespace-pre-wrap text-sm text-emerald-950">{item.adminReply}</p></div> : <p className="mt-3 text-xs text-stone-500">Waiting for the admin to reply.</p>}</article>)}</div>}
      </section>
    </>
  );

  const renderStore = () => (
    <>
      <SectionTitle
        eyebrow="Your storefront"
        title="My Store"
        description="Your store details are read from your account profile."
      />

      <div className="rounded-3xl border border-stone-200 bg-white p-6 sm:p-8">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-rose-50 text-rose-900">
            <Store size={36} />
          </div>
          <div className="flex-1">
            <p className="text-xs font-bold uppercase tracking-widest text-rose-800">Your store</p>
            <h3 className="mt-1 font-serif text-2xl text-rose-950">
              {profile?.shopName || profile?.storeName || "Store name not set"}
            </h3>
            <p className="mt-2 text-sm text-stone-500">
              {profile?.storeDescription || "No store description has been configured."}
            </p>
          </div>
        </div>

        <div className="mt-6 rounded-xl bg-[#faf7f5] p-4">
          <p className="text-xs text-stone-500">Store link</p>
          <p className="mt-2 break-all text-sm font-semibold text-rose-900">
            {storeUrl || "A store slug has not been assigned to your account."}
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <button
              onClick={() => copyText(storeUrl, "Store link copied.")}
              disabled={!storeUrl}
              className="inline-flex items-center gap-2 rounded-xl bg-rose-800 px-4 py-3 text-sm font-bold text-white disabled:opacity-50"
            >
              <Copy size={16} /> Copy store link
            </button>
            {storeUrl && (
              <>
                <a
                  href={storeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 rounded-xl border border-stone-200 px-4 py-3 text-sm font-bold text-rose-900 hover:bg-rose-50"
                >
                  <ArrowUpRight size={16} /> Open my store
                </a>
                <button onClick={openStoreShare} className="inline-flex items-center gap-2 rounded-xl border border-stone-200 px-4 py-3 text-sm font-bold text-rose-900 hover:bg-rose-50">
                  <Share2 size={16} /> Share my store
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );

  const content = {
    Overview: renderOverview,
    "Sarees & My Profit": renderProducts,
    "My Orders": renderOrders,
    "Wallet & Withdraw": renderWallet,
    "Enquire": renderEnquiry,
    "My Store": renderStore,
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#faf7f5] p-6">
        <div className="text-center">
          <LoaderCircle className="mx-auto animate-spin text-rose-800" size={34} />
          <p className="mt-3 text-sm text-stone-600">Loading your Firebase account...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  if (subscriptionExpired) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#faf7f5] px-4 py-10">
        <section className="w-full max-w-lg rounded-2xl border border-amber-200 bg-white p-7 text-center shadow-sm sm:p-10">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-800">
            <Clock size={26} />
          </div>
          <h1 className="mt-5 font-serif text-3xl text-rose-950">Your subscription has expired</h1>
          <p className="mt-3 text-sm leading-6 text-stone-600">
            Your reseller dashboard is temporarily locked because your ₹499 monthly subscription has expired.
            Renew your subscription to restore access.
          </p>
          <p className="mt-3 text-xs text-stone-500">
            Expired on {subscriptionExpiryDate.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}.
          </p>
          <button type="button" onClick={startSubscriptionRenewal} disabled={renewingSubscription} className="mt-6 inline-flex items-center justify-center rounded-xl bg-rose-900 px-6 py-3 text-sm font-semibold text-white hover:bg-rose-950 disabled:cursor-not-allowed disabled:opacity-60">
            {renewingSubscription ? "Opening secure payment…" : "Reactivate your store · ₹499/month"}
          </button>
          {renewalMessage && <p role="status" className="mt-4 text-sm leading-6 text-stone-700">{renewalMessage}</p>}
          <p className="mt-3 text-xs text-stone-500">After successful payment, your store subscription is extended for one month. Keep this page open until payment verification finishes.</p>
          <a href={`https://wa.me/916301898060?text=${encodeURIComponent("Hello Vasthra, I need help renewing my ₹499 monthly reseller subscription.")}`} target="_blank" rel="noreferrer" className="mt-3 inline-flex text-sm font-medium text-rose-900 underline">Need help? Contact Vasthra</a>
        </section>
      </main>
    );
  }

  if (!profile || isPending || isSuspended) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-[#faf7f5] p-5">
        <section className="w-full max-w-lg rounded-2xl border border-stone-200 bg-white p-7 text-center shadow-sm">
          <CircleHelp className="mx-auto text-amber-700" size={30} />
          <h1 className="mt-4 font-serif text-2xl text-rose-950">
            {isSuspended
              ? "Account unavailable"
              : isPending
                ? "Application under review"
                : "Account setup incomplete"}
          </h1>
          <p className="mt-2 text-sm leading-6 text-stone-600">
            {isSuspended
              ? "Your account is suspended or not approved. Please contact support."
              : isPending
                ? "Your reseller application is awaiting admin approval."
                : dataError || "No reseller profile was found for this account."}
          </p>

          <div className="mt-6 flex justify-center gap-3">
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 rounded-xl bg-rose-800 px-4 py-3 text-sm font-semibold text-white"
            >
              <RefreshCw size={16} /> Try again
            </button>
            <button onClick={logout} className="rounded-xl border border-stone-200 px-4 py-3 text-sm font-semibold">
              Log out
            </button>
          </div>
        </section>
      </main>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#faf7f5] text-stone-800">
      {mobileMenu && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            aria-label="Close menu overlay"
            onClick={() => setMobileMenu(false)}
            className="absolute inset-0 bg-black/50"
          />
          <aside className="relative flex h-full w-[min(300px,85vw)] flex-col overflow-y-auto bg-[#4c1020]">
            <button
              onClick={() => setMobileMenu(false)}
              aria-label="Close navigation"
              className="absolute right-4 top-5 z-10 rounded-lg p-2 text-white"
            >
              <X size={20} />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <div className="flex min-h-screen">
        <aside className="hidden w-[265px] shrink-0 flex-col bg-[#4c1020] lg:flex">
          {sidebar}
        </aside>

        <div className="min-w-0 flex-1">
          <header className="sticky top-0 z-30 border-b border-stone-200 bg-white/95 backdrop-blur">
            <div className="flex min-h-[64px] items-center justify-between gap-2 px-3 py-2 sm:min-h-[72px] sm:gap-3 sm:px-6 lg:px-8">
              <div className="flex min-w-0 items-center gap-3">
                <button
                  onClick={() => setMobileMenu(true)}
                  className="rounded-xl border border-stone-200 p-2 text-stone-700 lg:hidden"
                  aria-label="Open menu"
                >
                  <Menu size={21} />
                </button>
                <div className="min-w-0">
                  <p className="truncate text-xs text-stone-500">Reseller workspace</p>
                  <h1 className="truncate font-serif text-lg text-rose-950 sm:text-xl">{active}</h1>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-4">
                <div className="hidden items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-900 sm:flex">
                  <Wallet size={16} /> {money(walletBalance)}
                </div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setNotificationsOpen((open) => !open)}
                    aria-label="Notifications"
                    aria-expanded={notificationsOpen}
                    className="relative rounded-xl border border-stone-200 p-2.5 text-stone-600 hover:bg-stone-50"
                  >
                    <Bell size={19} />
                    {unreadNotifications.length > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-700 px-1 text-[10px] font-bold text-white">{unreadNotifications.length > 9 ? "9+" : unreadNotifications.length}</span>}
                  </button>
                  {notificationsOpen && (
                    <div className="absolute right-0 top-12 z-50 w-[min(90vw,360px)] overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-xl">
                      <div className="flex items-center justify-between border-b border-stone-100 px-4 py-3">
                        <p className="font-semibold text-stone-900">Notifications</p>
                        <button type="button" onClick={() => setNotificationsOpen(false)} aria-label="Close notifications"><X size={16}/></button>
                      </div>
                      <div className="max-h-96 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <p className="p-5 text-sm text-stone-500">No notifications yet. Order and payment updates will appear here when notifications are created.</p>
                        ) : [...notifications].sort((a, b) => {
                          const toMs = (v) => v?.toDate ? v.toDate().getTime() : new Date(v || 0).getTime();
                          return toMs(b.createdAt) - toMs(a.createdAt);
                        }).map((item) => (
                          <button key={item.id} type="button" onClick={() => markNotificationRead(item)} className={`block w-full border-b border-stone-100 px-4 py-3 text-left hover:bg-rose-50 ${item.isRead === true ? "bg-white" : "bg-amber-50/60"}`}>
                            <span className="flex items-start justify-between gap-3"><span className="text-sm font-semibold text-stone-900">{item.title || "Vasthra update"}</span>{item.isRead !== true && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-rose-700"/>}</span>
                            <span className="mt-1 block text-sm leading-5 text-stone-600">{item.message || item.body || "You have a new update."}</span>
                            <span className="mt-2 block text-[11px] text-stone-400">{dateLabel(item.createdAt)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div className="hidden h-9 w-9 items-center justify-center rounded-full bg-rose-100 text-sm font-bold text-rose-900 sm:flex">
                  {initials}
                </div>
              </div>
            </div>
          </header>

          <main className="mx-auto min-w-0 max-w-[1600px] p-3 sm:p-5 lg:p-8">
            {dataError && (
              <div role="alert" className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-6 text-red-800">
                {dataError}
              </div>
            )}

            {notice && (
              <div role="status" className="mb-5 flex items-start justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-900">
                <span className="break-all">{notice}</span>
                <button onClick={() => setNotice("")} aria-label="Dismiss message">
                  <X size={17} />
                </button>
              </div>
            )}

            {content[active]?.() || renderOverview()}

            <footer className="mt-10 border-t border-stone-200 py-5 text-center text-xs text-stone-500">
              © {new Date().getFullYear()} Vasthra · Reseller workspace
            </footer>
          </main>
        </div>
      </div>

      {shareModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="share-store-title" onClick={() => setShareModalOpen(false)}>
          <section className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-rose-800">Grow your business</p>
                <h2 id="share-store-title" className="mt-1 font-serif text-2xl text-rose-950">Share my store</h2>
                <p className="mt-2 text-sm text-stone-500">Send your boutique link to customers.</p>
              </div>
              <button onClick={() => setShareModalOpen(false)} aria-label="Close share options" className="rounded-lg p-2 text-stone-500 hover:bg-stone-100"><X size={20}/></button>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <a href={whatsappShareUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl bg-emerald-50 p-3 text-sm font-semibold text-emerald-800 hover:bg-emerald-100"><MessageCircle size={19}/> WhatsApp</a>
              <a href={facebookShareUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-xl bg-blue-50 p-3 text-sm font-semibold text-blue-800 hover:bg-blue-100"><FaFacebook size={19}/> Facebook</a>
              <button type="button" onClick={openInstagramShare} className="flex items-center gap-2 rounded-xl bg-pink-50 p-3 text-sm font-semibold text-pink-800 hover:bg-pink-100"><FaInstagram size={19}/> Instagram</button>
              <button onClick={async () => { await copyText(storeUrl, "Store link copied."); setShareModalOpen(false); }} className="flex items-center gap-2 rounded-xl bg-stone-100 p-3 text-sm font-semibold text-stone-800 hover:bg-stone-200"><Copy size={19}/> Copy link</button>
            </div>
            <div className="mt-4 rounded-xl bg-[#faf7f5] p-3">
              <p className="break-all text-xs text-stone-600">{storeUrl}</p>
              <a href={storeUrl || "#"} target="_blank" rel="noreferrer" onClick={() => setShareModalOpen(false)} className={`mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-rose-900 px-4 py-3 text-sm font-bold text-white hover:bg-rose-950 ${!storeUrl ? "pointer-events-none opacity-50" : ""}`}><Store size={16}/> Open my store</a>
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
